import { useState } from "react";
import { Link } from "react-router-dom";

import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FaqSection from "@/pages/hotel-apply/components/FaqSection";

type BusinessType = "hotel" | "pansiyon" | "butik" | "apart" | "kamp" | "firma";

const businessTypeLabels: Record<BusinessType, string> = {
  hotel: "Otel",
  pansiyon: "Pansiyon",
  butik: "Butik Otel",
  apart: "Apart / Daire",
  kamp: "Kamp & Glamping",
  firma: "Turizm Firması",
};

const businessTypeIcons: Record<BusinessType, string> = {
  hotel: "ri-building-2-line",
  pansiyon: "ri-home-heart-line",
  butik: "ri-hotel-line",
  apart: "ri-door-open-line",
  kamp: "ri-tent-line",
  firma: "ri-briefcase-line",
};

const cities = [
  "İstanbul", "Ankara", "İzmir", "Antalya", "Bursa", "Adana", "Gaziantep",
  "Konya", "Mersin", "Diyarbakır", "Kayseri", "Eskişehir", "Trabzon",
  "Nevşehir (Kapadokya)", "Muğla (Bodrum/Marmaris)", "Denizli (Pamukkale)",
  "Mardin", "Şanlıurfa", "Van", "Erzurum", "Rize", "Artvin", "Bolu",
  "Sakarya", "Çanakkale", "Edirne", "Balıkesir", "Aydın", "Manisa",
];

const HotelApplyPage = () => {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState<BusinessType | "">("");

  const [form, setForm] = useState({
    businessType: "",
    businessName: "",
    ownerName: "",
    email: "",
    phone: "",
    city: "",
    district: "",
    address: "",
    starRating: "",
    roomCount: "",
    priceRange: "",
    website: "",
    instagram: "",
    shortDesc: "",
    description: "",
    amenities: [] as string[],
    checkIn: "",
    checkOut: "",
    cancellationPolicy: "",
    languages: [] as string[],
    acceptTerms: false,
  });

  const amenityOptions = [
    "Ücretsiz WiFi", "Otopark", "Havuz", "Spa & Masaj", "Restoran",
    "Bar", "Kahvaltı Dahil", "Klima", "Balkon / Teras", "Deniz Manzarası",
    "Dağ Manzarası", "Evcil Hayvan Kabul", "Engelli Erişimi", "Çamaşırhane",
    "Bisiklet Kiralama", "Transfer Hizmeti", "Çocuk Oyun Alanı", "Fitness Salonu",
  ];

  const languageOptions = ["Türkçe", "İngilizce", "Almanca", "Rusça", "Arapça", "Fransızca"];

  const update = (field: string, value: string | boolean | string[]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (item: string) => {
    setForm((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(item)
        ? prev.amenities.filter((a) => a !== item)
        : [...prev.amenities, item],
    }));
  };

  const toggleLanguage = (lang: string) => {
    setForm((prev) => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...prev.languages, lang],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.acceptTerms) return;
    setSubmitting(true);

    // Save to localStorage for admin review
    const application = {
      id: Date.now().toString(),
      ...form,
      status: "pending",
      appliedAt: new Date().toISOString(),
      type: "accommodation",
    };
    const existing = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]");
    existing.push(application);
    localStorage.setItem("pendingAccommodations", JSON.stringify(existing));

    // Admin bildirim e-postası
    const adminEmail = localStorage.getItem("adminNotifEmail") || "admin@geziturkiyem.com";
    const adminSubject = `Yeni Konaklama Başvurusu: ${form.businessName}`;
    const adminBody = `Yeni bir konaklama başvurusu alındı!\n\nİşletme: ${form.businessName}\nTür: ${businessTypeLabels[form.businessType as BusinessType] || form.businessType}\nŞehir: ${form.city}, ${form.district}\nSahibi: ${form.ownerName}\nE-posta: ${form.email}\nTelefon: ${form.phone}\n\nAdmin panelinden inceleyebilirsiniz:\nhttps://geziturkiyem.com/admin`;
    window.open(`mailto:${adminEmail}?subject=${encodeURIComponent(adminSubject)}&body=${encodeURIComponent(adminBody)}`, "_blank");

    // Submit to form endpoint
    const body = new URLSearchParams();
    body.append("businessType", businessTypeLabels[form.businessType as BusinessType] || form.businessType);
    body.append("businessName", form.businessName);
    body.append("ownerName", form.ownerName);
    body.append("email", form.email);
    body.append("phone", form.phone);
    body.append("city", form.city);
    body.append("district", form.district);
    body.append("address", form.address);
    body.append("starRating", form.starRating);
    body.append("roomCount", form.roomCount);
    body.append("priceRange", form.priceRange);
    body.append("website", form.website);
    body.append("instagram", form.instagram);
    body.append("shortDesc", form.shortDesc);
    body.append("description", form.description);
    body.append("amenities", form.amenities.join(", "));
    body.append("checkIn", form.checkIn);
    body.append("checkOut", form.checkOut);
    body.append("cancellationPolicy", form.cancellationPolicy);
    body.append("languages", form.languages.join(", "));

    try {
      await fetch("https://readdy.ai/api/form/d7d5asckpdh0a4advrk0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch {
      // silent
    }

    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 1200);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center px-4 pt-20">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-emerald-100 rounded-full">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-4xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Başvurunuz Alındı!
            </h1>
            <p className="text-stone-500 text-sm leading-relaxed mb-2">
              <strong>{form.businessName}</strong> için üyelik başvurunuz başarıyla iletildi.
            </p>
            <p className="text-stone-400 text-sm leading-relaxed mb-8">
              Editörlerimiz başvurunuzu inceleyecek ve <strong>{form.email}</strong> adresinize en kısa sürede geri dönecektir. Onay süreci genellikle <strong>2-5 iş günü</strong> sürmektedir.
            </p>
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 mb-8 text-left">
              <p className="text-xs font-semibold text-amber-700 mb-3">Başvuru Özeti</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-stone-500">İşletme Türü</span>
                  <span className="font-medium text-stone-800">{businessTypeLabels[form.businessType as BusinessType]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">İşletme Adı</span>
                  <span className="font-medium text-stone-800">{form.businessName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Şehir</span>
                  <span className="font-medium text-stone-800">{form.city}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Durum</span>
                  <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium">İnceleniyor</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-center">
              <Link
                to="/"
                className="px-6 py-2.5 bg-stone-900 text-white text-sm font-semibold rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Ana Sayfaya Dön
              </Link>
              <Link
                to="/accommodations"
                className="px-6 py-2.5 border border-stone-200 text-stone-700 text-sm font-semibold rounded-xl hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Konaklama Rehberi
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 pb-12 bg-stone-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20boutique%20hotel%20Turkey%20cozy%20interior%20warm%20lighting%20elegant%20lobby%20reception%20wooden%20decor%20mountain%20view%20scenic&width=1400&height=500&seq=hotel-apply-hero&orientation=landscape"
            alt="Otel başvuru"
            className="w-full h-full object-cover object-top opacity-30"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-widest">
            Üyelik Başvurusu
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            İşletmenizi Platforma Ekleyin
          </h1>
          <p className="text-stone-300 text-sm max-w-xl mx-auto leading-relaxed">
            Otel, pansiyon veya turizm firmanızı Türkiye Seyahat Rehberi'nde listeleyin. Binlerce gezgine ulaşın.
          </p>

          {/* Steps */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold transition-all ${
                  step >= s ? "bg-amber-500 text-white" : "bg-stone-700 text-stone-400"
                }`}>
                  {step > s ? <i className="ri-check-line text-sm"></i> : s}
                </div>
                {s < 3 && <div className={`w-12 h-0.5 ${step > s ? "bg-amber-500" : "bg-stone-700"}`}></div>}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-16 mt-2">
            <span className={`text-xs ${step >= 1 ? "text-amber-300" : "text-stone-500"}`}>İşletme Bilgileri</span>
            <span className={`text-xs ${step >= 2 ? "text-amber-300" : "text-stone-500"}`}>Detaylar</span>
            <span className={`text-xs ${step >= 3 ? "text-amber-300" : "text-stone-500"}`}>Onay</span>
          </div>
        </div>
      </div>

      {/* Neden Üye Olmalısınız */}
      <div className="bg-white py-14 border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-3 uppercase tracking-widest">
              Neden Üye Olmalısınız?
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              İşletmenizi Büyütün, Daha Fazla Misafir Kazanın
            </h2>
            <p className="text-stone-400 text-sm mt-3 max-w-xl mx-auto">
              Türkiye Seyahat Rehberi'ne üye olan işletmeler ortalama %40 daha fazla rezervasyon alıyor.
            </p>
          </div>

          {/* Main benefits grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {[
              {
                icon: "ri-user-search-line",
                color: "bg-amber-100 text-amber-600",
                title: "12.000+ Aylık Ziyaretçi",
                desc: "Her ay binlerce gezgin platformumuzu kullanarak konaklama araştırıyor. İşletmeniz bu kitleye doğrudan ulaşır.",
              },
              {
                icon: "ri-phone-fill",
                color: "bg-emerald-100 text-emerald-600",
                title: "Telefon ile Doğrudan Rezervasyon",
                desc: "Ziyaretçiler telefon numaranızı görür, sizi doğrudan arar. Aracı olmadan, komisyonsuz rezervasyon.",
              },
              {
                icon: "ri-map-pin-2-line",
                color: "bg-rose-100 text-rose-600",
                title: "Harita Üzerinde Görünürlük",
                desc: "İşletmeniz interaktif haritada pin olarak gösterilir. Yakın çevredeki gezginler sizi kolayca bulur.",
              },
              {
                icon: "ri-star-line",
                color: "bg-amber-100 text-amber-600",
                title: "Misafir Yorumları & Puanlama",
                desc: "Gerçek misafir yorumları ve yıldız puanları ile güvenilirliğinizi artırın. Sosyal kanıt en güçlü pazarlama aracıdır.",
              },
              {
                icon: "ri-shield-check-line",
                color: "bg-stone-100 text-stone-600",
                title: "Admin Onaylı Güven Rozeti",
                desc: "Editörlerimiz tarafından onaylanan işletmeler 'Güvenilir İşletme' rozetiyle öne çıkar.",
              },
              {
                icon: "ri-whatsapp-line",
                color: "bg-emerald-100 text-emerald-600",
                title: "WhatsApp ile Anlık İletişim",
                desc: "Ziyaretçiler WhatsApp üzerinden de ulaşabilir. Hızlı yanıt, daha fazla rezervasyon demektir.",
              },
            ].map((b) => (
              <div key={b.title} className="bg-stone-50 rounded-2xl p-5 hover:bg-amber-50 transition-colors group">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-4 ${b.color}`}>
                  <i className={`${b.icon} text-xl`}></i>
                </div>
                <h3 className="font-bold text-stone-900 text-sm mb-2">{b.title}</h3>
                <p className="text-stone-500 text-xs leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>

          {/* Stats bar */}
          <div className="bg-stone-900 rounded-2xl p-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: "Ücretsiz", label: "Üyelik Başvurusu" },
              { value: "2-5 Gün", label: "Onay Süresi" },
              { value: "%0", label: "Komisyon" },
              { value: "7/24", label: "Çevrimiçi Görünürlük" },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-2xl font-bold text-amber-400" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                <p className="text-stone-400 text-xs mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
            {[
              {
                name: "Mehmet Y.",
                business: "Kapadokya Cave Suites",
                text: "Platforma katıldıktan sonra rezervasyonlarımız %35 arttı. Telefon aramaları çok daha fazla gelmeye başladı.",
                rating: 5,
              },
              {
                name: "Ayşe D.",
                business: "Mardin Taş Ev Pansiyon",
                text: "Ücretsiz üyelik ve komisyonsuz rezervasyon sistemi harika. Misafirler doğrudan bizi arıyor.",
                rating: 5,
              },
            ].map((t) => (
              <div key={t.name} className="bg-stone-50 rounded-2xl p-5 border border-stone-100">
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-stone-600 text-sm leading-relaxed mb-4 italic">&ldquo;{t.text}&rdquo;</p>
                <div>
                  <p className="font-semibold text-stone-900 text-sm">{t.name}</p>
                  <p className="text-stone-400 text-xs">{t.business}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gold Üyelik Tanıtım Kartı */}
      <div className="py-10 bg-white border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="relative rounded-2xl overflow-hidden border border-amber-300" style={{ background: "linear-gradient(135deg, #1c1917 0%, #292524 100%)" }}>
            <div className="absolute inset-0 opacity-10">
              <img
                src="https://readdy.ai/api/search-image?query=gold%20luxury%20shimmer%20abstract%20warm%20amber%20texture%20premium%20background%20elegant&width=1200&height=300&seq=gold-promo-bg&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-6 p-7">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-amber-500 to-yellow-400 rounded-2xl flex-shrink-0">
                  <i className="ri-vip-crown-fill text-white text-2xl"></i>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white text-xs font-bold px-3 py-0.5 rounded-full uppercase tracking-wide">Gold Üyelik</span>
                    <span className="text-stone-400 text-xs">Ücretsiz üyeliğin ötesi</span>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Daha Fazla Görünürlük İster misiniz?
                  </h3>
                  <p className="text-stone-300 text-sm leading-relaxed max-w-md">
                    Gold üyeler listede <strong className="text-amber-400">ilk sırada</strong> yer alır, altın rozet ve <strong className="text-amber-400">"Öne Çıkan"</strong> etiketiyle dikkat çeker. Ortalama <strong className="text-amber-400">3x</strong> daha fazla görüntülenme.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {["Altın rozet", "Liste başı sıralama", "Öne çıkan slider", "Öncelikli destek"].map((f) => (
                      <span key={f} className="flex items-center gap-1 text-xs text-amber-300">
                        <i className="ri-check-line text-amber-500"></i> {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <Link
                to="/gold-apply"
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500 text-white text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-vip-crown-fill"></i>
                Gold Üyelik Talep Et
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* SSS */}
      <FaqSection />

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-12">
        <form onSubmit={handleSubmit} data-readdy-form>

          {/* Step 1 */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  İşletme Türünü Seçin
                </h2>
                <p className="text-stone-400 text-sm mb-5">Hangi tür işletmeyi kaydetmek istiyorsunuz?</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(Object.keys(businessTypeLabels) as BusinessType[]).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => { setSelectedType(type); update("businessType", type); }}
                      className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                        selectedType === type
                          ? "border-amber-500 bg-amber-50"
                          : "border-stone-100 bg-white hover:border-amber-200"
                      }`}
                    >
                      <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${
                        selectedType === type ? "bg-amber-100" : "bg-stone-100"
                      }`}>
                        <i className={`${businessTypeIcons[type]} text-xl ${selectedType === type ? "text-amber-600" : "text-stone-500"}`}></i>
                      </div>
                      <span className={`text-xs font-semibold ${selectedType === type ? "text-amber-700" : "text-stone-600"}`}>
                        {businessTypeLabels[type]}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {selectedType && (
                <div className="space-y-4 animate-fade-in">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">İşletme Adı *</label>
                      <input
                        type="text"
                        name="businessName"
                        required
                        value={form.businessName}
                        onChange={(e) => update("businessName", e.target.value)}
                        placeholder="Örn: Kapadokya Cave Hotel"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">Yetkili Adı Soyadı *</label>
                      <input
                        type="text"
                        name="ownerName"
                        required
                        value={form.ownerName}
                        onChange={(e) => update("ownerName", e.target.value)}
                        placeholder="Ad Soyad"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">E-posta *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="ornek@email.com"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">Telefon *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="0532 000 00 00"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">Şehir *</label>
                      <select
                        name="city"
                        required
                        value={form.city}
                        onChange={(e) => update("city", e.target.value)}
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors bg-white"
                      >
                        <option value="">Şehir seçin</option>
                        {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">İlçe / Bölge *</label>
                      <input
                        type="text"
                        name="district"
                        required
                        value={form.district}
                        onChange={(e) => update("district", e.target.value)}
                        placeholder="İlçe veya bölge adı"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold text-stone-600 mb-1.5">Tam Adres *</label>
                      <input
                        type="text"
                        name="address"
                        required
                        value={form.address}
                        onChange={(e) => update("address", e.target.value)}
                        placeholder="Sokak, mahalle, posta kodu"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (!form.businessType || !form.businessName || !form.ownerName || !form.email || !form.phone || !form.city || !form.district || !form.address) {
                        return;
                      }
                      setStep(2);
                    }}
                    className="w-full py-3 bg-stone-900 text-white text-sm font-semibold rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Devam Et <i className="ri-arrow-right-line ml-1"></i>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  İşletme Detayları
                </h2>
                <p className="text-stone-400 text-sm mb-5">Misafirlerinize sunduğunuz hizmetleri anlatın.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(selectedType === "hotel" || selectedType === "butik") && (
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Yıldız Sayısı</label>
                    <select
                      name="starRating"
                      value={form.starRating}
                      onChange={(e) => update("starRating", e.target.value)}
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 bg-white"
                    >
                      <option value="">Seçin</option>
                      <option value="1">1 Yıldız</option>
                      <option value="2">2 Yıldız</option>
                      <option value="3">3 Yıldız</option>
                      <option value="4">4 Yıldız</option>
                      <option value="5">5 Yıldız</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Oda / Kapasite Sayısı</label>
                  <input
                    type="number"
                    name="roomCount"
                    value={form.roomCount}
                    onChange={(e) => update("roomCount", e.target.value)}
                    placeholder="Örn: 24"
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Fiyat Aralığı (Gecelik)</label>
                  <select
                    name="priceRange"
                    value={form.priceRange}
                    onChange={(e) => update("priceRange", e.target.value)}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 bg-white"
                  >
                    <option value="">Seçin</option>
                    <option value="₺ (500-1000₺)">₺ (500-1000₺)</option>
                    <option value="₺₺ (1000-2500₺)">₺₺ (1000-2500₺)</option>
                    <option value="₺₺₺ (2500-5000₺)">₺₺₺ (2500-5000₺)</option>
                    <option value="₺₺₺₺ (5000₺+)">₺₺₺₺ (5000₺+)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Check-in Saati</label>
                  <input
                    type="text"
                    name="checkIn"
                    value={form.checkIn}
                    onChange={(e) => update("checkIn", e.target.value)}
                    placeholder="Örn: 14:00"
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Check-out Saati</label>
                  <input
                    type="text"
                    name="checkOut"
                    value={form.checkOut}
                    onChange={(e) => update("checkOut", e.target.value)}
                    placeholder="Örn: 12:00"
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Website</label>
                  <input
                    type="url"
                    name="website"
                    value={form.website}
                    onChange={(e) => update("website", e.target.value)}
                    placeholder="https://www.oteliniz.com"
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Instagram</label>
                  <input
                    type="text"
                    name="instagram"
                    value={form.instagram}
                    onChange={(e) => update("instagram", e.target.value)}
                    placeholder="@oteliniz"
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Kısa Tanıtım (max 200 karakter) *</label>
                <input
                  type="text"
                  name="shortDesc"
                  required
                  maxLength={200}
                  value={form.shortDesc}
                  onChange={(e) => update("shortDesc", e.target.value)}
                  placeholder="İşletmenizi tek cümleyle tanıtın"
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                />
                <p className="text-xs text-stone-400 mt-1 text-right">{form.shortDesc.length}/200</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Detaylı Açıklama *</label>
                <textarea
                  name="description"
                  required
                  maxLength={500}
                  rows={4}
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  placeholder="İşletmenizi, sunduğunuz hizmetleri ve özelliklerinizi detaylıca anlatın..."
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors resize-none"
                />
                <p className="text-xs text-stone-400 mt-1 text-right">{form.description.length}/500</p>
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-3">Olanaklar & Hizmetler</label>
                <div className="flex flex-wrap gap-2">
                  {amenityOptions.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => toggleAmenity(item)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        form.amenities.includes(item)
                          ? "bg-amber-500 text-white"
                          : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                      }`}
                    >
                      {form.amenities.includes(item) && <i className="ri-check-line mr-1"></i>}
                      {item}
                    </button>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-3">Konuşulan Diller</label>
                <div className="flex flex-wrap gap-2">
                  {languageOptions.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLanguage(lang)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        form.languages.includes(lang)
                          ? "bg-stone-900 text-white"
                          : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-6 py-3 border border-stone-200 text-stone-700 text-sm font-semibold rounded-xl hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-1"></i> Geri
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!form.shortDesc || !form.description) return;
                    setStep(3);
                  }}
                  className="flex-1 py-3 bg-stone-900 text-white text-sm font-semibold rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Devam Et <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Başvuruyu Onaylayın
                </h2>
                <p className="text-stone-400 text-sm mb-5">Bilgilerinizi kontrol edin ve gönderin.</p>
              </div>

              {/* Summary */}
              <div className="bg-stone-50 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-3 pb-3 border-b border-stone-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl">
                    <i className={`${businessTypeIcons[form.businessType as BusinessType]} text-amber-600 text-lg`}></i>
                  </div>
                  <div>
                    <p className="font-bold text-stone-900">{form.businessName}</p>
                    <p className="text-xs text-stone-400">{businessTypeLabels[form.businessType as BusinessType]} · {form.city}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-stone-400">Yetkili</p>
                    <p className="font-medium text-stone-800">{form.ownerName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400">E-posta</p>
                    <p className="font-medium text-stone-800 truncate">{form.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400">Telefon</p>
                    <p className="font-medium text-stone-800">{form.phone}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400">Fiyat Aralığı</p>
                    <p className="font-medium text-stone-800">{form.priceRange || "Belirtilmedi"}</p>
                  </div>
                </div>
                {form.amenities.length > 0 && (
                  <div>
                    <p className="text-xs text-stone-400 mb-1.5">Seçilen Olanaklar</p>
                    <div className="flex flex-wrap gap-1">
                      {form.amenities.map((a) => (
                        <span key={a} className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">{a}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                    <i className="ri-information-line text-amber-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-800 mb-1">Onay Süreci Hakkında</p>
                    <ul className="text-xs text-amber-700 space-y-1 leading-relaxed">
                      <li>• Başvurunuz editörlerimiz tarafından 2-5 iş günü içinde incelenir</li>
                      <li>• Onay/red kararı e-posta ile bildirilir</li>
                      <li>• Onaylanan işletmeler konaklama rehberinde listelenir</li>
                      <li>• Ziyaretçiler form üzerinden doğrudan size ulaşabilir</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => update("acceptTerms", !form.acceptTerms)}
                  className={`w-5 h-5 flex-shrink-0 mt-0.5 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
                    form.acceptTerms ? "bg-amber-500 border-amber-500" : "border-stone-300"
                  }`}
                >
                  {form.acceptTerms && <i className="ri-check-line text-white text-xs"></i>}
                </button>
                <p className="text-xs text-stone-500 leading-relaxed">
                  Verdiğim bilgilerin doğru olduğunu, platform kullanım koşullarını okuduğumu ve kabul ettiğimi onaylıyorum. Başvurum onaylandığında işletmem platforma eklenecektir.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-6 py-3 border border-stone-200 text-stone-700 text-sm font-semibold rounded-xl hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-1"></i> Geri
                </button>
                <button
                  type="submit"
                  disabled={!form.acceptTerms || submitting}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Gönderiliyor...</>
                  ) : (
                    <><i className="ri-send-plane-line"></i> Başvuruyu Gönder</>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default HotelApplyPage;
